import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import DuplicateTemplateModal from '../components/common/DuplicateTemplateModal';
import CreateOrganizationModal from '../components/common/CreateOrganizationModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import type { Template, UserResponse } from '../types';
import type { Organization } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [allUserResponses, setAllUserResponses] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState<Template | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch templates and organizations in parallel
        const [fetchedTemplates, fetchedOrganizations] = await Promise.all([
          apiService.getAllTemplates(),
          apiService.getAllOrganizations()
        ]);
        
        setTemplates(fetchedTemplates);
        setOrganizations(fetchedOrganizations);
        
        // Fetch user responses for all organizations
        const allResponses: UserResponse[] = [];
        for (const org of fetchedOrganizations) {
          try {
            const orgResponses = await apiService.getOrganizationResponses(org.id);
            allResponses.push(...orgResponses);
          } catch (responseErr) {
            console.warn(`Failed to fetch responses for organization ${org.id}:`, responseErr);
            // If organization responses API is not available, try to fetch individual user responses
            for (const user of org.users) {
              try {
                const userResponses = await apiService.getUserResponses(user.id);
                allResponses.push(...userResponses);
              } catch (userErr) {
                console.warn(`Failed to fetch responses for user ${user.id}:`, userErr);
              }
            }
          }
        }
        
        setAllUserResponses(allResponses);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load data');
        // Fallback to static data
        setTemplates([{
          id: 1,
          name: 'Manager Skills Development',
          category: 'Management',
          description: 'Self & Personnel Management',
          is_active: true,
          created_at: '2025-09-10T00:00:00Z',
          updated_at: '2025-09-10T00:00:00Z',
          questions: []
        }]);
        setOrganizations([{
          id: 1,
          name: 'Organization 1',
          users: [],
          templates: [],
          created_at: '2025-09-10T00:00:00Z',
          updated_at: '2025-09-10T00:00:00Z',
        }]);
        setAllUserResponses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate average completion for an organization's templates
  const getOrganizationAverageCompletion = (organization: Organization): string => {
    // Filter out soft-deleted users
    const activeUsers = organization.users.filter(user => !user.deleted_at);
    
    if (!activeUsers.length || !organization.templates.length) {
      return '0%';
    }

    let totalPercentage = 0;
    let userCount = 0;

    // Calculate completion percentage for each active user across all org templates
    for (const user of activeUsers) {
      let userTotalQuestions = 0;
      let userCompletedQuestions = 0;

      // Get completion for each template assigned to this organization
      for (const template of organization.templates) {
        const templateQuestions = template.questions?.length || 0;
        userTotalQuestions += templateQuestions;

        // Get unique question IDs that this user has responded to for this template
        const userTemplateResponses = allUserResponses.filter(
          response => response.user_id === user.id && response.template_id === template.id
        );
        const questionsWithResponses = new Set(
          userTemplateResponses.map(response => response.question_id)
        );
        userCompletedQuestions += questionsWithResponses.size;
      }

      // Calculate user's overall completion percentage
      const userPercentage = userTotalQuestions > 0 
        ? (userCompletedQuestions / userTotalQuestions) * 100 
        : 0;
      
      totalPercentage += userPercentage;
      userCount++;
    }

    // Return average completion percentage across all users
    const averagePercentage = userCount > 0 ? totalPercentage / userCount : 0;
    return `${Math.round(averagePercentage)}%`;
  };

  // Calculate average completion for a specific template across all users
  const getTemplateAverageCompletion = (template: Template): string => {
    const totalQuestions = template.questions?.length || 0;
    if (totalQuestions === 0) {
      return '0%';
    }

    // Get all users who have been assigned this template (find through organizations)
    const assignedUsers = organizations
      .filter(org => org.templates.some(t => t.id === template.id))
      .flatMap(org => org.users);

    if (assignedUsers.length === 0) {
      return '0%';
    }

    let totalPercentage = 0;
    let userCount = 0;

    // Calculate completion percentage for each assigned user
    for (const user of assignedUsers) {
      const userResponses = allUserResponses.filter(
        response => response.user_id === user.id && response.template_id === template.id
      );
      
      // Get unique question IDs that this user has responded to
      const questionsWithResponses = new Set(
        userResponses.map(response => response.question_id)
      );
      
      const completedQuestions = questionsWithResponses.size;
      const userPercentage = (completedQuestions / totalQuestions) * 100;
      
      totalPercentage += userPercentage;
      userCount++;
    }

    // Return average completion percentage across all assigned users
    const averagePercentage = userCount > 0 ? totalPercentage / userCount : 0;
    return `${Math.round(averagePercentage)}%`;
  };

  const handleDuplicateTemplate = (template: Template) => {
    setTemplateToDuplicate(template);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async (name: string, description: string, organizationId?: number) => {
    if (!templateToDuplicate) return;
    
    try {
      setIsDuplicating(true);
      
      // Call the duplicate API with the template ID, name, description, and optional organization ID
      const duplicatedTemplate = await apiService.duplicateTemplate(templateToDuplicate.id, name, description, organizationId);
      
      const orgMessage = organizationId 
        ? ` and assigned to ${organizations.find(org => org.id === organizationId)?.name || 'selected organization'}`
        : '';
      
      toast.success(`Successfully duplicated "${templateToDuplicate.name}"${orgMessage}`);
      
      // Refresh the templates list to show the new duplicated template
      const updatedTemplates = await apiService.getAllTemplates();
      setTemplates(updatedTemplates);

      // Refresh organizations data which includes templates
      const updatedOrganizations = await apiService.getAllOrganizations();
      setOrganizations(updatedOrganizations);
      
      // Close modal
      setShowDuplicateModal(false);
      setTemplateToDuplicate(null);
    } catch (err) {
      console.error('Failed to duplicate template:', err);
      toast.error('Failed to duplicate worksheet');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setTemplateToDuplicate(null);
  };

  const handleCreateOrganization = async (name: string) => {
    try {
      setIsCreatingOrg(true);
      
      // Call the create organization API
      const newOrganization = await apiService.createOrganization(name);
      
      toast.success(`Successfully created "${name}" organization`);
      
      // Refresh the organizations list to show the new organization
      const updatedOrganizations = await apiService.getAllOrganizations();
      setOrganizations(updatedOrganizations);
      
      // Close modal
      setShowCreateOrgModal(false);
    } catch (err) {
      console.error('Failed to create organization:', err);
      toast.error('Failed to create organization');
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleCloseCreateOrgModal = () => {
    setShowCreateOrgModal(false);
  };

  const handleDeleteTemplate = async (template: Template) => {
    // Show SweetAlert confirmation dialog
    const result = await Swal.fire({
      title: 'Delete Worksheet',
      html: `Are you sure you want to delete <strong>"${template.name}"</strong>?<br><br>This will permanently delete the worksheet and all its questions. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      customClass: {
        confirmButton: 'swal2-confirm-delete',
        cancelButton: 'swal2-cancel'
      }
    });
    
    if (!result.isConfirmed) return;
    
    try {
      setDeletingTemplateId(template.id);
      
      // Call the delete API
      await apiService.deleteTemplate(template.id);
      
      toast.success(`Successfully deleted "${template.name}"`);
      
      // Remove the template from the local state
      setTemplates(prevTemplates => prevTemplates.filter(t => t.id !== template.id));
      
    } catch (err) {
      console.error('Failed to delete template:', err);
      
      // Show error message with SweetAlert
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to delete worksheet. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading worksheets...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Admin Dashboard
          </h1>
          <p className="mt-2 text-gray-500">
            Manage organizations, base career development worksheets, and view client progress
          </p>
        </div>
        
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Note</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{error} - Showing fallback data.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-4 mb-4">
          <div className="flex items-center justify-between pb-4 border-b-2 border-gray-300">
            <div className="flex items-center">
              <h2 className="text-2xl font-bold text-gray-900 mr-2">
                Organization
              </h2>
              <div className="w-8 h-8 min-w-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                {organizations.length}
              </div>
            </div>
            <button
              onClick={() => setShowCreateOrgModal(true)}
              className="inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-white bg-secondary-500 hover:bg-secondary-700 hover:border-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 transition-colors"
              title="Create new organization"
            >
              <svg className="w-5 h-5 mr-2 text-purple-600 font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create New Organization
            </button>
          </div>
          {organizations.map((org) => {
            const averageCompletion = getOrganizationAverageCompletion(org);
            const activeUserCount = org.users.filter(user => !user.deleted_at).length;

            return (
              <div key={org.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 border-l-4 border-l-secondary-500">
                <div className="flex relative">
                  <span className="text-4xl my-2 mr-4">🏢</span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{org.name}</h2>
                    <p className="text-sm text-gray-500 mt-1">Last active: {new Date(org.updated_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })} {new Date(org.updated_at).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit', 
                      hour12: true  })}</p>
                  </div>
                </div>
                <div className="flex relative">
                  <div className="my-2 mr-4">👥 <span className="font-normal text-gray-400 text-sm">{activeUserCount} {activeUserCount === 1 ? 'User' : 'Users'}</span></div>
                    <div className="my-2 mr-4">📄 <span className="font-normal text-gray-400 text-sm">{org.templates.length > 0 ? `${org.templates.length} ${org.templates.length === 1 ? 'Worksheet' : 'Worksheets'}` : 'N/A'}</span></div>
                  <div className="my-2 mr-4"><span className="font-normal text-gray-400 text-sm">✓ {averageCompletion} avg completion</span></div>
                </div>
                <button
                  onClick={() => navigate(`/organization/${org.id}`)}
                  className="top-4 right-4 mt-3 inline-flex items-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 hover:border-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  title={`View ${org.name} details`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Organization Details
                </button>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="flex items-center pb-4 border-b-2 border-gray-300">
            <h2 className="text-2xl font-bold text-gray-900 mr-2">
              Base Career Development Worksheets
            </h2>
          </div>
          {templates.filter(template => template.id === 1).map((template, index) => {
            const isBaseTemplate = template.id === 1;
            const isDuplicated = template.id !== 1;
            const borderColor = isDuplicated ? 'border-l-purple-500' : 'border-l-primary-500';
            const templateCompletion = getTemplateAverageCompletion(template);
            
            return (
              <div key={template.id} className={`bg-white border border-gray-200 rounded-lg shadow-sm border-l-4 ${borderColor} flex relative`}>
                <div className="flex-shrink-0 p-4 flex items-center justify-center bg-gray-50 w-16">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      {/* Template Badges */}
                      {isBaseTemplate && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-medium bg-green-600 text-white">
                          Base Worksheet
                        </span>
                      )}
                      {isDuplicated && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-medium bg-purple-600 text-white">
                          Client Worksheet
                        </span>
                      )}
                    </div>
                  </div>
                
                <p className="text-gray-600 mb-4">{template.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <p>Last edited: {new Date(template.updated_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })} | {template.questions?.length || 0} questions configured</p>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <button
                    className="inline-flex items-center px-4 py-2.5 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    onClick={() => navigate(`/edit-template/${template.id}`)}
                  >
                    Edit Worksheet
                  </button>
                  <button
                    onClick={() => handleDuplicateTemplate(template)}
                    className="inline-flex items-center px-4 py-2.5 border border-secondary-300 text-base font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 transition-colors"
                    title={`Duplicate ${template.name}`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Duplicate Worksheet
                  </button>
                  {template.id !== 1 && (
                    <button
                      onClick={() => handleDeleteTemplate(template)}
                      disabled={deletingTemplateId === template.id}
                      className="inline-flex items-center px-4 py-2.5 border border-red-300 text-base font-medium rounded-md text-red-700 bg-white hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={`Delete ${template.name}`}
                    >
                      {deletingTemplateId === template.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete Worksheet
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      </div>
      
      {/* Duplicate Template Modal */}
      <DuplicateTemplateModal
        isOpen={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        onConfirm={handleConfirmDuplicate}
        template={templateToDuplicate}
        organizations={organizations}
        isLoading={isDuplicating}
      />

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={handleCloseCreateOrgModal}
        onConfirm={handleCreateOrganization}
        isLoading={isCreatingOrg}
      />
    </Layout>
  );
};

export default Dashboard;